import type { MessageTemplate } from '@app/services/admin';

import { placeholderToTokens } from './welcome-message-utils';

export interface WelcomeMessageFormData {
  beginningMessage: string;
  endingMessage: string;
  isEndingMessageEnabled: boolean;
  confirmationAssigned: string;
  confirmationUnassigned: string;
  followupMessage: string;
  isFollowupEnabled: boolean;
  followupDelay: number;
  frBeginningMessage: string;
  frEndingMessage: string;
  frConfirmationAssigned: string;
  frConfirmationUnassigned: string;
  frFollowupMessage: string;
}

// Language code stored in MessageTemplate.secondaryMessages.
export const SECONDARY_LANGUAGE = 'fr-ca';

// Literal union rather than `keyof WelcomeMessageFormData` so `value[field]`
// is known to be a string.
export type EnglishMessageFieldName =
  | 'beginningMessage'
  | 'endingMessage'
  | 'confirmationAssigned'
  | 'confirmationUnassigned'
  | 'followupMessage';

export type FrenchMessageFieldName =
  | 'frBeginningMessage'
  | 'frEndingMessage'
  | 'frConfirmationAssigned'
  | 'frConfirmationUnassigned'
  | 'frFollowupMessage';

export const ENGLISH_FIELD_NAMES: EnglishMessageFieldName[] = [
  'beginningMessage',
  'endingMessage',
  'confirmationAssigned',
  'confirmationUnassigned',
  'followupMessage',
];

export const FRENCH_FIELD_NAMES: FrenchMessageFieldName[] = [
  'frBeginningMessage',
  'frEndingMessage',
  'frConfirmationAssigned',
  'frConfirmationUnassigned',
  'frFollowupMessage',
];

export type StaleSourceField = EnglishMessageFieldName | FrenchMessageFieldName;

// Maps each message field to its counterpart in the other language.
export const FIELD_PAIR: Record<StaleSourceField, StaleSourceField> = {
  beginningMessage: 'frBeginningMessage',
  endingMessage: 'frEndingMessage',
  confirmationAssigned: 'frConfirmationAssigned',
  confirmationUnassigned: 'frConfirmationUnassigned',
  followupMessage: 'frFollowupMessage',
  frBeginningMessage: 'beginningMessage',
  frEndingMessage: 'endingMessage',
  frConfirmationAssigned: 'confirmationAssigned',
  frConfirmationUnassigned: 'confirmationUnassigned',
  frFollowupMessage: 'followupMessage',
};

// Field names as constants so a typo fails to compile.
export const WELCOME_FIELD = {
  isEndingMessageEnabled: 'isEndingMessageEnabled',
  isFollowupEnabled: 'isFollowupEnabled',
  frBeginning: 'frBeginningMessage',
  frEnding: 'frEndingMessage',
  frConfirmationAssigned: 'frConfirmationAssigned',
  frConfirmationUnassigned: 'frConfirmationUnassigned',
  frFollowup: 'frFollowupMessage',
} as const satisfies Record<string, keyof WelcomeMessageFormData>;

// legalMessage in mention format; the split point for a combined body.
export const LEGAL_MESSAGE_MARKER = '@[legalMessage](legalMessage)';

// Plain-$token form used in the preview (mention markup is editor-only).
export const LEGAL_MESSAGE_PLAIN_TOKEN = '$legalMessage';

// Splits a mention-format body on the legal marker into [beginning, ending].
export const splitOnLegalMessageMarker = (body: string): [string, string] => {
  const [beginning = '', ending = ''] = body.split(LEGAL_MESSAGE_MARKER);
  return [beginning, ending];
};

// Body plus the tokens[] its ${TOKEN} placeholders resolve against.
export interface SecondaryMessageContent {
  body: string;
  tokens: Array<{ token: string }>;
}

// Returns the fr-ca secondary entry for a template, or undefined when absent.
export const pickSecondary = (
  template: MessageTemplate | undefined,
  language: string,
) => template?.secondaryMessages?.find((m) => m.language === language);

// Returns the fr-ca secondary-authoring body for a template, or '' when absent.
export const pickSecondaryBody = (
  template: MessageTemplate | undefined,
  language: string,
): string => pickSecondary(template, language)?.body ?? '';

// A template with no tokens is left alone rather than mention-converted.
export const hydratePrimaryBody = (
  template: MessageTemplate | undefined,
): string => {
  const body = template?.body || '';
  if (!template?.tokens) return body;
  return placeholderToTokens(body, template.tokens);
};

// Rehydrates a stored secondary into editor (mention) format.
export const hydrateSecondaryBody = (
  template: MessageTemplate | undefined,
  language: string,
): string => {
  const secondary = pickSecondary(template, language);
  if (!secondary?.body) return '';
  return placeholderToTokens(secondary.body, secondary.tokens ?? []);
};

// Attaches, replaces, or drops the fr-ca secondary. A blank body still emits an
// empty array — an omitted secondaryMessages reads as "no change" server-side,
// so clearing must be explicit. Other languages are preserved.
export const withSecondary = (
  template: MessageTemplate,
  secondary: SecondaryMessageContent,
): MessageTemplate => {
  const others = (template.secondaryMessages ?? []).filter(
    (m) => m.language !== SECONDARY_LANGUAGE,
  );

  if (!secondary?.body?.trim()) {
    return { ...template, secondaryMessages: others };
  }

  return {
    ...template,
    secondaryMessages: [
      ...others,
      // populateTemplate only expands ${TOKEN} + tokens[], never inline $token.
      {
        language: SECONDARY_LANGUAGE,
        body: secondary.body,
        tokens: secondary.tokens ?? [],
      },
    ],
  };
};

/**
 * Assembles the fr-ca initial welcome for save. Returns '' only when there is
 * nothing to persist (clear the secondary). An enabled ending with content
 * must still assemble even if beginning is blank — otherwise Save drops a
 * French ending that is still present in the form.
 */
export const assembleSecondaryInitialWelcome = (
  beginning: string,
  ending: string,
  endingEnabled: boolean,
): string => {
  const hasContent = !!beginning.trim() || (endingEnabled && !!ending.trim());
  if (!hasContent) return '';
  return endingEnabled
    ? `${beginning} ${LEGAL_MESSAGE_MARKER} ${ending}`
    : `${beginning} ${LEGAL_MESSAGE_MARKER}`;
};

const ENDING_STALE_FIELDS: StaleSourceField[] = [
  'endingMessage',
  'frEndingMessage',
];
const FOLLOWUP_STALE_FIELDS: StaleSourceField[] = [
  'followupMessage',
  'frFollowupMessage',
];

/**
 * Stale fields that still belong to the authored flow. Ending/follow-up drop
 * out when their toggles are off so the banner and language warnings don't
 * linger for content that is no longer sent.
 */
export const applicableStaleFields = (
  staleFields: readonly StaleSourceField[],
  {
    isEndingMessageEnabled,
    isFollowupEnabled,
  }: Pick<
    WelcomeMessageFormData,
    'isEndingMessageEnabled' | 'isFollowupEnabled'
  >,
): StaleSourceField[] =>
  staleFields.filter((field) => {
    if (!isEndingMessageEnabled && ENDING_STALE_FIELDS.includes(field)) {
      return false;
    }
    if (!isFollowupEnabled && FOLLOWUP_STALE_FIELDS.includes(field)) {
      return false;
    }
    return true;
  });

export type MissingLanguageMessageKey =
  | 'beginning'
  | 'confirmationAssigned'
  | 'confirmationUnassigned'
  | 'followup'
  | 'ending';

export type WelcomeLanguage = 'en' | typeof SECONDARY_LANGUAGE;

export interface MissingLanguageError {
  messageKey: MissingLanguageMessageKey;
  missingLanguage: WelcomeLanguage;
}

export const MISSING_LANGUAGE_MESSAGE_NAME_KEY: Record<
  MissingLanguageMessageKey,
  string
> = {
  beginning: 'welcomeNotOptedIn.missingLanguageMessageBeginning',
  ending: 'welcomeNotOptedIn.missingLanguageMessageEnding',
  confirmationAssigned:
    'welcomeNotOptedIn.missingLanguageMessageConfirmationAssigned',
  confirmationUnassigned:
    'welcomeNotOptedIn.missingLanguageMessageConfirmationUnassigned',
  followup: 'welcomeNotOptedIn.missingLanguageMessageFollowup',
};

// One key per language: French adjective agreement can't be interpolated.
export const MISSING_LANGUAGE_BANNER_TITLE_KEY: Record<
  WelcomeLanguage,
  string
> = {
  en: 'welcomeNotOptedIn.missingLanguageEnglish',
  [SECONDARY_LANGUAGE]: 'welcomeNotOptedIn.missingLanguageFrench',
};

export const GO_TO_LANGUAGE_KEY: Record<WelcomeLanguage, string> = {
  en: 'welcomeNotOptedIn.goToEnglish',
  [SECONDARY_LANGUAGE]: 'welcomeNotOptedIn.goToFrenchCanadian',
};

// Shorter than the banner title: the field itself names the message.
export const MISSING_LANGUAGE_FIELD_KEY: Record<WelcomeLanguage, string> = {
  en: 'welcomeNotOptedIn.missingLanguageFieldEnglish',
  [SECONDARY_LANGUAGE]: 'welcomeNotOptedIn.missingLanguageFieldFrench',
};

export type ConfirmationMessageKey = Extract<
  MissingLanguageMessageKey,
  'confirmationAssigned' | 'confirmationUnassigned'
>;

// Which confirmation input a missing-language error belongs to.
export const CONFIRMATION_FIELD: Record<
  ConfirmationMessageKey,
  Record<WelcomeLanguage, StaleSourceField>
> = {
  confirmationAssigned: {
    en: 'confirmationAssigned',
    [SECONDARY_LANGUAGE]: 'frConfirmationAssigned',
  },
  confirmationUnassigned: {
    en: 'confirmationUnassigned',
    [SECONDARY_LANGUAGE]: 'frConfirmationUnassigned',
  },
};

export const CONFIRMATION_VIEW_BY_MESSAGE_KEY: Record<
  ConfirmationMessageKey,
  'assigned' | 'unassigned'
> = {
  confirmationAssigned: 'assigned',
  confirmationUnassigned: 'unassigned',
};

export const isConfirmationMessageKey = (
  messageKey: MissingLanguageMessageKey,
): messageKey is ConfirmationMessageKey => messageKey in CONFIRMATION_FIELD;

// Client-only guard: both languages required, symmetrically. A fully blank pair
// isn't flagged. Optional messages count only when their toggle is on.
export const getMissingLanguageError = (
  values: WelcomeMessageFormData,
): MissingLanguageError | null => {
  const checks: Array<
    [
      applicable: boolean,
      en: string,
      fr: string,
      messageKey: MissingLanguageMessageKey,
    ]
  > = [
    [true, values.beginningMessage, values.frBeginningMessage, 'beginning'],
    [
      true,
      values.confirmationAssigned,
      values.frConfirmationAssigned,
      'confirmationAssigned',
    ],
    [
      true,
      values.confirmationUnassigned,
      values.frConfirmationUnassigned,
      'confirmationUnassigned',
    ],
    [
      values.isFollowupEnabled,
      values.followupMessage,
      values.frFollowupMessage,
      'followup',
    ],
    [
      values.isEndingMessageEnabled,
      values.endingMessage,
      values.frEndingMessage,
      'ending',
    ],
  ];

  const missing = checks.find(
    ([applicable, en, fr]) => applicable && !!en?.trim() !== !!fr?.trim(),
  );

  if (!missing) return null;

  const [, en, , messageKey] = missing;

  return {
    messageKey,
    missingLanguage: en?.trim() ? SECONDARY_LANGUAGE : 'en',
  };
};
